import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {finalize, map, merge, Observable, of, Subscription, switchMap, tap} from 'rxjs';
import * as LessonsV3 from 'src/app/col/model/v3/lessons';
import { ApiLessonFlag } from 'src/app/col/model/rest-model';
import { Dates, TimeUnits } from 'src/app/utils/calendar-utils';
import {ModalComponent} from "../../../../utils/modal/modal.component";
import { WebSocketService } from 'src/app/col/services/web-socket.service';
import { environment } from 'src/environments/environment';
import {ActivatedRoute} from "@angular/router";
import {NativeServiceApiProvider} from "../../../../services/native-api-provider.service";

@Component({
    selector: 'app-lesson-commited-progress',
    templateUrl: './lesson-commited-progress.component.html',
    styleUrls: ['./lesson-commited-progress.component.scss'],
    standalone: false
})
export class LessonCommitedProgressComponent implements OnInit, OnDestroy {
  private static readonly COURSE_TO_GIFT_CODE: Record<string, string> = {
    'en.s1': 'EEBookS1r3',
    'en.s2': 'EEBookS2r3',
    'en.s3': 'EEBookS3r3',
    'en.s4': 'EEBookS4r3',
    'en.s5': 'EEBookS5r3',
    'en.s6': 'EEBookS6r3',
    'en.s7': 'EEBookS7r3',
    'en.s8': 'EEBookS8r3',
    'en.s9': 'EEBookS9r3',
    'en.s10': 'EEBookS10r3',
    'en.s11': 'EEBookS11r3',
    'en.s12': 'EEBookS12r3',
    'sp.s1': 'SEBookS1',
    'sp.s2': 'SEBookS2',
    'sp.s3': 'SEBookS3',
    'sp.s4': 'SEBookS4',
    'sp.s5': 'SEBookS5',
    'sp.s6': 'SEBookS6',
    'en.bs.s1': 'EBEBookS1',
    'en.ch.s1': 'EKidEBookS1',
    'en.ch.s2': 'EKidEBookS2',
    'en.ch.s3': 'EKidEBookS3',
    'en.ch.s4': 'EKidEBookS4',
    'en.ch.s5': 'EKidEBookS5',
    'en.ch.s6': 'EKidEBookS6',
  };

  @ViewChild('migrateToNewEnglishConfirmationModal', {static: true}) migrateToNewEnglishConfirmationModal: ModalComponent;
  private _lessonDetails: LessonsV3.LessonView;
  private _studentId: number;
  private _lessonId: number;
  private _schoolId: number;
  private courseCodeChecked: string = null;
  private hasGift = false;
  private _currentProgress: LessonsV3.CallanProgressDetails | null = null;
  private lessonProductCode: string | null = null;
  private lessonProductName = '';
  private lessonCourseCode: string | null = null;
  private lessonCourseName = '';

  productCourses: LessonsV3.Course[];
  studentContext: LessonsV3.ProductContextDetails | null;
  studentCourseContexts: LessonsV3.ColCourseContext[] = [];
  courseLoaded: LessonsV3.Course | null;
  lessonStatus: LessonsV3.LessonStatus | null = null;
  lessonType: LessonsV3.LessonType | null = null;
  lessonMessage: LessonsV3.ApiLessonMessage = {
    lessonComment: '',
    privateLessonComment: '',
    message: '',
  };
  lessonStudentMessage = '';
  lessonFlags: LessonsV3.ApiLessonFlag[] = [];
  lessonProgressCommitted: LessonsV3.CallanProgressDetails = {
    start: 0,
    reading: 0,
    nwp: 0,
    headwords: '',
    productVersion: null,
  };
  isComplete: boolean;
  isRunning: boolean;
  isComing: boolean;
  shouldBeStarted: boolean;
  newLessonType: LessonsV3.LessonType;
  cancelable: boolean;
  plannedLessonStart: Date;
  shouldBeFinished: boolean;
  plannedLessonFinish: Date;
  progressCommitLimit: Date;
  isMigratingInProgress: boolean = false;

  wsSubscription: Subscription
  _progressIsBeingSaved = false

  @Input()
  set lessonDetails(lessonDetails: LessonsV3.LessonView) {
    if (lessonDetails) {
      this._lessonDetails = lessonDetails;
      this.syncDerivedFieldsFromLesson();
      this.setLessonDetails();
      this.reloadStudentContext()
    }
  }

  get lessonDetails() {
    return this._lessonDetails;
  }

  @Input()
  set currentProgress(progress: LessonsV3.CallanProgressDetails) {
    if (!progress) {
      return;
    }
    this._currentProgress = progress;
    this.lessonProgressCommitted = this._currentProgress;
  }

  get currentProgress() {
    return this._currentProgress;
  }
  @Input()
  set lessonMessageInput(message: LessonsV3.ApiLessonMessage | null) {
    this.lessonMessage = message ?? {
      lessonComment: '',
      privateLessonComment: '',
      message: '',
    };
    this.lessonStudentMessage = this.lessonMessage.message ?? '';
  }

  @Input()
  set lessonFlagsInput(flags: LessonsV3.ApiLessonFlag[] | null) {
    this.lessonFlags = flags ?? [];
  }

  @Input()
  set lessonProductInput(product: LessonsV3.V3Product | null) {
    this.lessonProductCode = product?.code ?? null;
    this.lessonProductName = product?.name ?? '';
    this.reloadStudentContext()
  }

  @Input() teacherId: number;
  @Input() canUpdateProgress: boolean = false;
  @Output() showCancelLesson = new EventEmitter();
  @Output() showFlags = new EventEmitter();
  @Output() updateLessonDetails = new EventEmitter();

  constructor(
    private webSocket: WebSocketService,
    private route: ActivatedRoute,
    private provider: NativeServiceApiProvider
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(
      tap((params) => {
        const routeSchoolId = params.get('schoolId');
        this._schoolId = routeSchoolId ? +routeSchoolId : +(this.route.snapshot.data?.['schoolId'] ?? 0);
        this._studentId = +params.get('studentId');
        this._lessonId = +params.get('lessonId');
        if (this.lessonDetails) {
          this.reloadStudentContext();
        }
      }),
      tap(_ => this.listenToWebSocket())
    ).subscribe()
  }

  ngOnDestroy(): void {
    this.wsSubscription?.unsubscribe();
  }

  showCancelLessonModal() {
    this.showCancelLesson.emit();
  }
  showFlagsModal() {
    this.showFlags.emit();
  }

  getFlagDescription(type: string) {
    return ApiLessonFlag.getFLagDescription(type);
  }

  getStudentAvailableCourses(): Observable<LessonsV3.Course[]> {
    const schoolId = this.getSchoolId();
    const productCode = this.lessonProductCode;
    if (!schoolId || !productCode) {
      return of([] as LessonsV3.Course[]).pipe(tap((courses) => (this.productCourses = courses)));
    }
    return this.provider.colV3().pipe(
      switchMap((api) => api.getProductCourses(schoolId, productCode)),
      tap((courses) => {
        this.productCourses = courses;
        this.syncLessonCourseNameFromCourses();
      })
    );
  }

  reloadStudentContext() {
    const schoolId = this.getSchoolId();
    const productCode = this.lessonProductCode;
    if (!schoolId || !productCode || !this._studentId) {
      return;
    }
    this.getStudentAvailableCourses().pipe(
      switchMap(() =>
        this.provider.colV3().pipe(
          switchMap((api) =>
            api.getProductContexts(
              schoolId,
              this._studentId,
              productCode
            ).pipe(
              switchMap((contexts) =>
                api
                  .getCourseContexts(
                    schoolId,
                    this._studentId,
                    productCode,
                    true
                  )
                  .pipe(
                    map((courseContexts) => ({contexts, courseContexts}))
                  )
              )
            )
          )
        )
      ),
      map(({contexts, courseContexts}) => {
        const context = contexts?.[0] ?? null;
        const currentCourseCode = courseContexts?.[0]?.course?.code;
        const currentCourse = currentCourseCode
          ? (this.productCourses ?? []).find((c) => c.code === currentCourseCode) ?? null
          : null;
        return {context, currentCourse, courseContexts: courseContexts ?? []};
      }),
      tap(({context, currentCourse, courseContexts}) => {
        this.studentContext = context;
        this.courseLoaded = currentCourse;
        this.studentCourseContexts = courseContexts;
      })
    ).subscribe();
  }

  listenToWebSocket() {
    if (!this.wsSubscription && this._studentId) {
      this.wsSubscription = this.webSocket.establish(environment.eventsWsEndpoint).pipe(
        switchMap(connection => merge(
          connection.subscribe(`/col/student/${this._studentId}/LessonListUpdate`),
          connection.subscribe(`/col/student/${this._studentId}/LessonUpdate`)
        )),
      ).subscribe(() => {
        this.emitLessonDetailsUpdate()
      })
    }
  }

  setLessonDetails() {
    const lessonStatus = this.lessonStatus;
    this.isComplete = lessonStatus === LessonsV3.LessonStatus.Complete;
    this.isRunning = lessonStatus === LessonsV3.LessonStatus.InProgress;
    this.isComing =
      lessonStatus === LessonsV3.LessonStatus.Initializing ||
      lessonStatus === LessonsV3.LessonStatus.Booked ||
      lessonStatus === LessonsV3.LessonStatus.Due;
    this.plannedLessonStart = new Date(
      this.lessonDetails.lessonMetric.plannedStartDate
    );
    this.shouldBeStarted =
      this.plannedLessonStart.getTime() < new Date().getTime() && this.isComing;
    this.newLessonType = this.lessonType;
    let mayBeStartedTime =
      Dates.diff(new Date(), this.plannedLessonStart) <
      TimeUnits.Minutes(15).toMilis();
    this.cancelable =
      (
        lessonStatus === LessonsV3.LessonStatus.Initializing ||
        lessonStatus === LessonsV3.LessonStatus.Booked
      ) &&
      !this.shouldBeStarted &&
      !mayBeStartedTime;
    this.plannedLessonFinish = new Date(
      this.plannedLessonStart.getTime() +
        this.lessonDetails.lessonMetric.plannedDuration
    );
    this.progressCommitLimit = new Date(
      this.plannedLessonStart.getTime() + 1000 * 60 * 60 * 6
    );
    this.shouldBeFinished =
      this.plannedLessonFinish.getTime() < new Date().getTime() &&
      this.shouldBeStarted;
    this.canUpdateProgress =
      this.shouldBeFinished ||
      this.isRunning ||
      this.shouldBeStarted ||
      (this.isComplete &&
        this.progressCommitLimit.getTime() > new Date().getTime());
  }

  private prepareToStart() {
    let observable: Observable<null> = of(null);

    if (this.lessonStatus === LessonsV3.LessonStatus.Booked) {
      observable = observable.pipe(
        switchMap(() =>
          this.provider.colV3().pipe(
            switchMap((api) => api.updateLessonStatus(this._lessonId, LessonsV3.LessonStatus.Due)),
            map(() => null)
          )
        )
      );
    }
    if (this.isComing) {
      observable = observable.pipe(
        switchMap(() =>
          this.provider.colV3().pipe(
            switchMap((api) => api.updateLessonStatus(this._lessonId, LessonsV3.LessonStatus.InProgress)),
            map(() => null)
          )
        )
      );
    }
    return observable;
  }

  commitLesson() {
    this._progressIsBeingSaved = true
    let observable = this.prepareToStart();

    if (this.newLessonType !== this.lessonType) {
      observable = observable.pipe(
        switchMap(() =>
          this.provider.colV3().pipe(
            switchMap((api) => api.updateLessonType(this._lessonId, this.newLessonType)),
            map(() => null)
          )
        )
      );
    }

    const progressOperation =
      this.isComing || this.isRunning
        ? this.finishLesson(this.lessonProgressCommitted)
        : this.updateLessonProgress(this.lessonProgressCommitted);
    observable = observable.pipe(switchMap(() => progressOperation));

    observable
      .pipe(
        switchMap(() =>
          this.provider.colV3().pipe(
            switchMap((api) => api.updateProductContext(
              this._schoolId,
              this._studentId,
              this.lessonProductCode ?? '',
              {
                productCode: this.lessonProductCode,
                nextLessonType: this.studentContext?.nextLessonType ?? this.newLessonType,
                productVersion: this.studentContext?.productVersion ?? null,
              }
            ))
          )
        ),
        switchMap((_) => this.postLessonMessages())
      )
      .subscribe(_ => {
        this._progressIsBeingSaved = false
        this.emitLessonDetailsUpdate()
      });
  }

  private emitLessonDetailsUpdate() {
    if(!this._progressIsBeingSaved) {
      this.updateLessonDetails.emit()
    }
  }

  private updateLessonProgress(progress: LessonsV3.CallanProgressDetails) {
    const dto: LessonsV3.LessonCallanProgressDetailsDto = { ...progress, '@type': 'callan' };
    return this.provider.colV3().pipe(
      switchMap((api) => api.updateLessonProgress(this._lessonId, dto)),
      map(() => null)
    );
  }

  private finishLesson(progress: LessonsV3.CallanProgressDetails) {
    const dto: LessonsV3.LessonCallanProgressDetailsDto = { ...progress, '@type': 'callan' };
    return this.provider.colV3().pipe(
      switchMap((api) => api.updateLessonStatus(this._lessonId, LessonsV3.LessonStatus.Complete).pipe(
        switchMap(() => api.updateLessonProgress(this._lessonId, dto))
      )),
      map(() => null)
    );
  }

  private postLessonMessages() {
    let observable: Observable<LessonsV3.ApiLessonMessage | null> = of(null);
    if (this.lessonMessage) {
      observable = observable.pipe(
        switchMap(() =>
          this.provider.colV3().pipe(
            switchMap((api) => api.updateLessonComments(this._lessonId, this.lessonMessage))
          )
        )
      );
    }

    if (this.lessonStudentMessage) {
      observable = observable.pipe(
        switchMap(() =>
          this.provider.colV3().pipe(
            switchMap((api) => api.updateLessonComments(
              this._lessonId,
              { lessonComment: '', privateLessonComment: '', message: this.lessonStudentMessage },
              this._studentId
            ))
          )
        )
      );
    }

    return observable;
  }

  public studentNoShow() {
    let observable = this.prepareToStart();
    this._progressIsBeingSaved = true
    observable
      .pipe(
        switchMap(() => this.registerNoStudentFlag()),
        tap(() => {
          this._progressIsBeingSaved = false
          this.emitLessonDetailsUpdate()
        })
      )
      .subscribe();
  }

  hasStudentNoShowFlag() {
    return this.lessonFlags.length > 0 && this.lessonFlags.some((f) => f.flagType === LessonsV3.LessonFlagType.StudentNotShow);
  }

  haveGift() {
    if (!this.isComplete || !this.studentContext || !this.courseLoaded) return false;
    if (this.lessonType !== LessonsV3.LessonType.Prova) return false;
    if (
      !this.courseCodeChecked ||
      this.courseCodeChecked !== this.courseLoaded.code
    ) {
      this.courseCodeChecked = this.courseLoaded.code;
      this.hasGift = false;
      if (!this.courseCodeChecked) return false;
      const giftCode = this.resolveGiftCode(this.courseCodeChecked);
      if (!giftCode || !this._schoolId || !this._studentId) return false;
      this.provider
        .colV3()
        .pipe(switchMap(api => api.getStudentGifts(this._schoolId, this._studentId, true, "casa")))
        .subscribe((gifts) => {
          this.hasGift = !gifts?.some((gift) => gift?.productVariant?.mappedId === giftCode);
        });
    }
    return this.hasGift;
  }

  giveGift() {
    if (this.haveGift()) {
      const giftCode = this.resolveGiftCode(this.courseCodeChecked);
      if (!giftCode || !this._schoolId || !this._studentId) return;
      this.provider
        .colV3()
        .pipe(
          switchMap(api => api.createStudentGift(this._schoolId, this._studentId, {
            productVariant: {mappedId: giftCode},
            amount: 1,
            currency: 'gbp',
            name: `prova gift for ${this.courseCodeChecked}`,
            description: null,
            metadata: null,
            expiryDate: null,
          }, 'casa'))
        )
        .subscribe((_) => (this.hasGift = false));
    }
  }

  getGiftEbookName() {
    return { ebookName: this.courseLoaded?.name || '' }
  }

  private registerNoStudentFlag() {
    return this.provider.colV3().pipe(
      switchMap((api) => api.createLessonFlag(
        this._lessonId,
        { flagType: LessonsV3.LessonFlagType.StudentNotShow },
        null,
        this.teacherId
      ))
    );
  }
  isOldEnglishVersion() {
    return this.lessonDetails.course.code.startsWith('en.s') && this.studentContext?.productVersion == null
  }

  migrateToNewEnglish() {
    this.isMigratingInProgress = true
    this.provider.colV3().pipe(
      switchMap((api) => api.updateProductContext(
        this._schoolId,
        this._studentId,
        this.lessonProductCode ?? '',
        {
          productCode: this.lessonProductCode,
          nextLessonType: this.studentContext?.nextLessonType ?? LessonsV3.LessonType.Standard,
          productVersion: '3',
        }
      )),
      finalize(() => {
        this.isMigratingInProgress = false
        this.migrateToNewEnglishConfirmationModal.hide()
      })
    ).subscribe()
  }

  private resolveGiftCode(courseCode: string): string | null {
    if (!courseCode) return null;
    return LessonCommitedProgressComponent.COURSE_TO_GIFT_CODE[courseCode] ?? null;
  }

  private syncDerivedFieldsFromLesson(): void {
    if (!this.lessonDetails) {
      return;
    }
    this.lessonStatus = (this.lessonDetails.status as LessonsV3.LessonStatus) ?? null;
    this.lessonType = (this.lessonDetails.type as LessonsV3.LessonType) ?? null;
    this.lessonCourseCode = this.lessonDetails.course?.code ?? null;
    this.syncLessonCourseNameFromCourses();
  }


  private findCourseName(courseCode: string | null | undefined): string | null {
    if (!courseCode || !this.productCourses?.length) {
      return null;
    }
    return this.productCourses.find((course) => course.code === courseCode)?.name ?? null;
  }

  private syncLessonCourseNameFromCourses(): void {
    if (this.lessonCourseName || !this.lessonCourseCode) {
      return;
    }
    this.lessonCourseName = this.findCourseName(this.lessonCourseCode) ?? '';
  }

  private getSchoolId(): number {
    return this._schoolId || +(this.route.snapshot.data?.['schoolId'] ?? 0);
  }
}
